import React, { useEffect, useState } from "react";
import { Expandable } from "@/components/Expandable";
import { api } from "@/lib/api";
import { History, Clock, Upload, Trash2, FileX2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import SideSheet from "@/components/SideSheet";

/**
 * Provenance footer for attendance cards.
 * - "Based on N records · Updated Xh ago by sweekar · View audit"
 * - Clicking "View audit" opens a side sheet with the last 50 actions.
 */
const ProvenanceFooter = ({ meta, filteredCount }) => {
    const [showAudit, setShowAudit] = useState(false);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadAudit = async () => {
        setLoading(true);
        try {
            const r = await api.get("/attendance/audit-log?limit=50");
            setEntries(r.data?.entries || []);
        } catch {
            setEntries([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (showAudit) loadAudit(); }, [showAudit]);

    const updated = meta?.latest_uploaded_at
        ? (() => {
            try { return formatDistanceToNow(parseISO(meta.latest_uploaded_at), { addSuffix: true }); }
            catch { return "recently"; }
        })()
        : null;
    const updatedBy = (meta?.latest_uploaded_by || "").split("@")[0];

    return (
        <>
            <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500 pt-3 border-t border-slate-100 flex-wrap gap-2" data-testid="provenance-footer">
                <div className="inline-flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-slate-400" />
                    Based on <span className="text-slate-700 font-semibold tabular-nums">{filteredCount}</span>
                    {filteredCount === 1 ? " record" : " records"}
                    {typeof meta?.total_records === "number" && meta.total_records !== filteredCount && (
                        <> of <span className="text-slate-600 tabular-nums">{meta.total_records}</span> in scope</>
                    )}
                </div>
                {updated && (
                    <div className="inline-flex items-center gap-1.5">
                        Updated {updated}{updatedBy && <> by <span className="text-slate-700">{updatedBy}</span></>}
                        <button
                            onClick={() => setShowAudit(true)}
                            className="ml-2 inline-flex items-center gap-1 text-[#25908A] hover:text-[#2EB4AC] font-medium"
                            data-testid="view-audit-btn"
                        >
                            <History className="h-3 w-3" /> View audit
                        </button>
                    </div>
                )}
            </div>

            <SideSheet
                open={showAudit}
                onClose={() => setShowAudit(false)}
                title="Attendance audit log"
                subtitle="Last 50 changes, most recent first"
                testid="audit-log-sheet"
                width={520}
            >
                {loading ? (
                    <div className="p-10 text-center text-slate-500 text-sm">Loading audit trail…</div>
                ) : entries.length === 0 ? (
                    <div className="p-10 text-center text-slate-500 text-sm">No recorded activity yet.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {entries.map((e) => {
                            const Icon = e.action === "upload" ? Upload : e.action === "bulk_delete" ? FileX2 : Trash2;
                            const color = e.action === "upload" ? "bg-green-50 text-green-700 border-green-200"
                                : e.action === "bulk_delete" ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-amber-50 text-amber-700 border-amber-200";
                            const when = (() => {
                                try { return formatDistanceToNow(parseISO(e.ts), { addSuffix: true }); }
                                catch { return e.ts; }
                            })();
                            return (
                                <div key={e.id} className="px-6 py-4 flex items-start gap-3" data-testid={`audit-entry-${e.id}`}>
                                    <div className={`h-8 w-8 shrink-0 rounded-lg border flex items-center justify-center ${color}`}>
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm">
                                            <span className="font-medium text-slate-900">{(e.actor_email || "—").split("@")[0]}</span>{" "}
                                            <span className="text-slate-500">
                                                {e.action === "upload" && <>uploaded attendance — {e.meta?.created || 0} created, {e.meta?.updated || 0} updated</>}
                                                {e.action === "delete" && <>deleted attendance for <span className="text-slate-700">{e.meta?.employee}</span> · {e.meta?.month}</>}
                                                {e.action === "bulk_delete" && <>bulk-deleted <span className="text-slate-700 font-semibold">{e.meta?.deleted}</span> records</>}
                                            </span>
                                        </div>
                                        {e.action === "bulk_delete" && e.meta?.records?.length > 0 && (
                                            <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-1">
                                                <Expandable
                                                    items={e.meta.records}
                                                    limit={6}
                                                    className="text-[#25908A] font-semibold"
                                                    testid="provenance-records-more"
                                                    render={(r, i) => (
                                                        <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                                                            {r.employee} · {r.month}
                                                        </span>
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <div className="text-[11px] text-slate-400 mt-1">{when}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </SideSheet>
        </>
    );
};

export default ProvenanceFooter;
